import {FormSubmissionStepStatus} from "../../../common/models/FormSubmissionStepStatus";
import {ArrayHelper} from "../../../common/helpers/arrayHelper";
import {PermitteeType} from "~/enums/permitteeType";
import Decorator from "../../../common/models/decorator";
import {IUser} from "../../../common/interfaces/userInterfaces";
import History from "../../../common/models/formSubmission/history";
import Correspondence from "../../../common/models/formSubmission/correspondence";
import {ActivationStatusType, ActivationStatusTypeDisplay} from "../../admin/models/activationStatusType";
import {userActivationStatusDisplay} from "~/deactivatable";

export interface IFormSubmission {
  id: string;
  custom_submission_id: string;
  display_submission_id: string;
  c_at: string;
  u_at: string;
  status: number;
  pending_completers: { id: string }[];
  needs_assignment: boolean;
  completer_group_name: string;
  dependent_form_name: string;
  status_updated_at: string;
  current_status_title: string;
  show_as_anonymous_submitter: boolean;
  submitter_name: string;
  submitter_email: string;
  next_incomplete_step_submitter_type: number;
  next_incomplete_step_workflow_step_id: string;
  last_completed_step_submitter_type: number;
  proxy_submitter: {id: string, name: string, email: string};
  applicant_info: {id: string, name: string, email: string};
  submitted_at: string;
  history?: History;
  correspondences?: Correspondence[];
  current_application_status_name: string;
  entity_id: string;
  entity_name: string;
}

const defaultUser = (): IUser => {
  return {
    id: null,
    name: null,
    first_name: null,
    last_name: null,
    email: null
  };
};

interface IQuery {
  q: string;
  status: string;
}

export default class SubmissionDecorator extends Decorator implements IFormSubmission {
  object: object;
  status: number;
  pending_completers: { id: string, name: string }[] = [];
  needs_assignment: boolean;
  completer_group_id: string;
  completer_group_name: string;
  dependent_form_name: string;
  id: string;
  custom_submission_id: string;
  display_submission_id: string;
  c_at: string;
  u_at: string;
  status_updated_at: string;
  current_status_title: string;
  show_as_anonymous_submitter: boolean;
  submitter_id: string;
  submitter_name: string;
  submitter_email: string;
  submitter_activation_status: number;
  past_completers: Array<any>;
  current_completers: Array<any>;
  next_incomplete_step_id: string;
  next_incomplete_step_workflow_step_id: string;
  next_incomplete_step_submitter_type: number;
  next_incomplete_step_status_viewer_reference_ids: string[];
  next_incomplete_step_num_times_begun: string;
  last_completed_step_submitter_type: number;
  last_completed_step_status_viewer_reference_ids: string[];
  steps_so_far_viewer_reference_ids: string[];
  proxy_submitter = {id: null, name: '', email: ''};
  applicant_info = {id: null, name: '', email: ''};
  submitted_by = {id: null, name: '', email: ''};
  submitted_at: string;
  user: IUser = defaultUser();
  current_application_status_name: string;
  entity_id: string;
  entity_name: string;
  form_submission_steps: Array<any>;

  static get displayName(): string {
    return 'SubmissionDecorator';
  }

  updateFrom(submissionObj: IFormSubmission, user = null): this {
    super.updateFrom(submissionObj);
    if (user) {
      this.user = user;
    }
    return this;
  }

  get submissionPath(): string {
    return `/submissions/${this.id}/edit`;
  }

  get userIsAssigned(): boolean {
    const completer = this.current_completers.filter( val => {
      return val.id === this.user.id;
    });
    return completer.length > 0;
  }

  get needsGroup(): boolean {
    if (!this.completer_group_name || this.needsCurrentUser) {
      return false;
    }
    return this.pendingCompletersOtherThanUser.length === 0 && this.completer_group_name.length > 0;
  }

  get statusTimePrefix(): string {
    return this.isFinalStatus ? 'on' : 'since';
  }

  get statusTime(): string {
    return this.isDraft ? this.c_at : this.status_updated_at;
  }

  get statusClass(): string {
    switch (this.status) {
      case FormSubmissionStepStatus.approved:
      case FormSubmissionStepStatus.completed:
        return 'status-success';
      case FormSubmissionStepStatus.rejected:
        return 'status-danger';
      case FormSubmissionStepStatus.pendingApproval:
      case FormSubmissionStepStatus.pendingApprovalAndFormSection:
      case FormSubmissionStepStatus.pendingFormSection:
      case FormSubmissionStepStatus.pendingFormCompletion:
      case FormSubmissionStepStatus.pendingPayment:
        return 'status-info';
      default:
        return 'status-info';
    }
  }

  get completersDisplayNames(): string {
    if (this.needs_assignment && !this.isPendingOnUser) {
      return this.completer_group_name;
    } else {
      return ArrayHelper.toSentence(
        this.pending_completers
          .map(c => userActivationStatusDisplay(c))
      );
    }
  }

  get completersDisplayNamesForStatus(): string {
    if (this.next_incomplete_step_submitter_type === PermitteeType.thirdParty && this.pendingCompletersOtherThanUser.length > 1) {
        return 'multiple 3rd party completers';
    } else {
      return this.completersDisplayNames;
    }
  }

  get isStatusViewer() : boolean {
    return this.next_incomplete_step_status_viewer_reference_ids.includes(this.user.id) || this.last_completed_step_status_viewer_reference_ids.includes(this.user.id);
  }

  private get needsCurrentUser(): boolean {
    return this.currentUserIsCompleter;
  }

  private get currentUserIsCompleter(): boolean {
    return this.pending_completers.some(c => c.id === this.user.id);
  }

  private get isDraft(): boolean {
    return this.status === FormSubmissionStepStatus.draft;
  }

  private get isFinalStatus(): boolean {
    return [
      FormSubmissionStepStatus.completed,
      FormSubmissionStepStatus.approved,
      FormSubmissionStepStatus.rejected
    ].includes(this.status);
  }

  private get pendingCompletersOtherThanUser() {
    return this.pending_completers.filter(c => c.id !== this.user.id);
  }

  private get isPendingOnUser() {
    return this.pending_completers.some(c => c.id === this.user.id);
  }

  get submittedBy(): string {
    return this.submittedByProxy || this.submittedByEntity || this.submittedBySubmitter
  }

  private get submittedByProxy(): string {
    if (this.submitted_by.id) {
      if (this.submitted_by.id !== this.submitter_id) {
        return submittedOnBehalfOf(this.submitted_by, this.applicant_info)
      }
      return // don't fall back to proxy_submitter if submitted_by is present
    }

    if (this.proxy_submitter.id) {
      return submittedOnBehalfOf(this.proxy_submitter, this.applicant_info)
    }
  }

  private get submittedByEntity(): string {
     if (this.entity_id) {
      return submittedBy({name: this.entity_name, email: this.submitter_email})
    }
  }

  private get submittedBySubmitter(): string {
    return submittedBy({
      name: this.submitter_name,
      email: this.submitter_email,
    }, { anonymous: this.show_as_anonymous_submitter })
  }

  get submittedByWActivationStatus(): string {
    if (this.submitter_activation_status === ActivationStatusType.inactive) {
      return `${this.submittedBy} (${ActivationStatusTypeDisplay[ActivationStatusType.inactive]})`
    } else {
      return this.submittedBy
    }
  }

  formattedValueForElement(elementId: string): string {
    if (this['formatted_values'] !== undefined) {
      return this['formatted_values'][elementId];
    } else {
      return this[elementId];
    }
  };

  get currentStep(): string {
    return this.form_submission_steps.find(step => step.id === this.next_incomplete_step_id)
  }
}

function submittedOnBehalfOf(proxy: {name: string, email: string}, applicant: {name: string, email?: string}): string {
  let applicantInfoText = applicant.name;
  if (applicant.email) {
    applicantInfoText += ` (${applicant.email})`;
  }
  return `${proxy.name} (${proxy.email}) on behalf of ${applicantInfoText}`;
}

function submittedBy(submitter: {name: string, email: string}, {anonymous}: {anonymous?: boolean} = {}): string {
  if (anonymous || !submitter.name) return 'Anonymous Submitter'

  return `${submitter.name} <br> ${submitter.email}`;
}